import type { HttpClient } from '../http.js';
import type {
  Offer,
  OfferPreview,
  OfferPayResult,
  ProcessPaymentResult,
} from '../types.js';
import type { PaymentModule } from './payment.js';

/**
 * Offers — upsells, downsells, order bumps, and post-purchase offers.
 *
 * Endpoints align with the plugin-sdk patterns:
 *   GET  /api/v1/offers/:id                             — single offer
 *   GET  /api/v1/stores/:storeId/offers                 — list store offers
 *   POST /api/v1/offers/:offerId/preview                — preview pricing
 *   POST /api/v1/offers/:offerId/pay-preview            — pay a previewed offer (one-click)
 *   POST /api/v1/checkout-sessions/:id/toggle-order-bump — toggle order bump
 *   GET  /api/v1/post-purchase/:orderId/offers          — post-purchase offers for an order
 *   POST /api/v1/orders/:orderId/post-purchase-offers/:offerId/accept  — accept
 *   POST /api/v1/orders/:orderId/post-purchase-offers/:offerId/decline — decline
 */
export class OffersModule {
  constructor(
    private readonly http: HttpClient,
    private readonly storeId: string,
    private readonly payment?: PaymentModule,
  ) {}

  // ---------------------------------------------------------------------------
  // Offer loading
  // ---------------------------------------------------------------------------

  /** Get a single offer by ID. */
  async getOffer(offerId: string): Promise<Offer> {
    return this.http.get<Offer>(`/api/v1/offers/${offerId}`);
  }

  /** List offers for the current store. Optionally filter by type or a set of IDs. */
  async listOffers(opts?: {
    type?: 'upsell' | 'orderbump';
    ids?: string[];
  }): Promise<Offer[]> {
    let url = `/api/v1/stores/${this.storeId}/offers`;

    const queryParts: string[] = [];
    if (opts?.type) queryParts.push(`type=${encodeURIComponent(opts.type)}`);
    if (opts?.ids?.length) {
      for (const id of opts.ids) queryParts.push(`ids=${encodeURIComponent(id)}`);
    }
    if (queryParts.length) url += `?${queryParts.join('&')}`;

    const res = await this.http.get<{ offers: Offer[] }>(url);
    return res.offers ?? [];
  }

  // ---------------------------------------------------------------------------
  // Preview & pay (one-click upsell flow from plugin-sdk)
  // ---------------------------------------------------------------------------

  /** Preview offer pricing. Returns line items with amounts and totals. */
  async previewOffer(opts: {
    offerId: string;
    currency?: string;
    lineItems?: Array<{ lineItemId?: string; productId?: string; variantId: string; quantity: number }>;
  }): Promise<OfferPreview> {
    return this.http.post<OfferPreview>(`/api/v1/offers/${opts.offerId}/preview`, {
      offerId: opts.offerId,
      currency: opts.currency ?? '',
      lineItems: opts.lineItems,
    });
  }

  /**
   * Pay a previously previewed offer (one-click charge).
   * Used after a main order so the stored payment instrument is reused.
   *
   * **Low-level API.** Returns the raw `OfferPayResult` — `requireAction`,
   * polling, and 3DS redirects are NOT handled. For most cases, use
   * {@link processOfferPayment} which orchestrates the full lifecycle.
   */
  async payPreviewedOffer(opts: {
    offerId: string;
    mainOrderId: string;
    currency?: string;
    lineItems?: Array<{ lineItemId?: string; productId?: string; variantId: string; quantity: number }>;
    returnUrl?: string;
    paymentInstrumentId?: string;
  }): Promise<OfferPayResult> {
    return this.http.post<OfferPayResult>(`/api/v1/offers/${opts.offerId}/pay-preview`, {
      offerId: opts.offerId,
      currency: opts.currency ?? '',
      lineItems: opts.lineItems,
      returnUrl: opts.returnUrl ?? (typeof window !== 'undefined' ? window.location.href : ''),
      mainOrderId: opts.mainOrderId,
      paymentInstrumentId: opts.paymentInstrumentId,
    });
  }

  /**
   * One-click upsell with full lifecycle handling.
   *
   * Like `payment.processPayment()` but for previewed offers: charges the
   * stored instrument from `mainOrderId`, runs any 3DS challenge or bank
   * redirect, polls until terminal, and returns a typed
   * `ProcessPaymentResult` (`succeeded` / `requires_redirect` /
   * `failed` / `pending`).
   *
   * Use this instead of the lower-level `payPreviewedOffer()` whenever you
   * don't need to inspect the raw `OfferPayResult` shape — which is most
   * of the time.
   *
   * @example
   * ```ts
   * const result = await tagada.offers.processOfferPayment({
   *   offerId: 'offer_xxx',
   *   mainOrderId: order.id,
   *   returnUrl: window.location.href,
   * });
   *
   * switch (result.status) {
   *   case 'succeeded':
   *     window.location.href = '/upsell-2';
   *     break;
   *   case 'requires_redirect':
   *     window.location.href = result.redirectUrl;
   *     break;
   *   case 'failed':
   *     showError(result.error);
   *     break;
   * }
   * ```
   */
  async processOfferPayment(opts: {
    offerId: string;
    mainOrderId: string;
    currency?: string;
    lineItems?: Array<{
      lineItemId?: string;
      productId?: string;
      variantId: string;
      quantity: number;
    }>;
    returnUrl?: string;
    paymentInstrumentId?: string;
  }): Promise<ProcessPaymentResult> {
    if (!this.payment) {
      throw new Error(
        'OffersModule: no PaymentModule injected. ' +
          'Use createHeadlessClient() instead of constructing OffersModule directly.',
      );
    }
    const returnUrl =
      opts.returnUrl ?? (typeof window !== 'undefined' ? window.location.href : undefined);
    const payResult = await this.payPreviewedOffer({ ...opts, returnUrl });
    return this.payment.finalize(
      { payment: payResult.payment, order: payResult.order },
      { returnUrl },
    );
  }

  // ---------------------------------------------------------------------------
  // Order bumps (checkout-session scoped)
  // ---------------------------------------------------------------------------

  /** Toggle an order bump on/off within a checkout session. */
  async toggleOrderBump(opts: {
    checkoutSessionId: string;
    orderBumpOfferId: string;
    selected: boolean;
  }): Promise<unknown> {
    return this.http.post(
      `/api/v1/checkout-sessions/${opts.checkoutSessionId}/toggle-order-bump`,
      {
        orderBumpOfferId: opts.orderBumpOfferId,
        selected: opts.selected,
      },
    );
  }

  // ---------------------------------------------------------------------------
  // Post-purchase offers (order-scoped)
  // ---------------------------------------------------------------------------

  /** List post-purchase offers for an order. */
  async listPostPurchaseOffers(orderId: string): Promise<Offer[]> {
    const res = await this.http.get<Offer[]>(`/api/v1/post-purchase/${orderId}/offers`);
    return Array.isArray(res) ? res : [];
  }

  /** Accept a post-purchase offer. */
  async acceptPostPurchaseOffer(opts: {
    orderId: string;
    offerId: string;
    items?: Array<{ variantId: string; quantity: number }>;
  }): Promise<{ success: boolean; error?: string }> {
    return this.http.post(
      `/api/v1/orders/${opts.orderId}/post-purchase-offers/${opts.offerId}/accept`,
      { items: opts.items ?? [] },
    );
  }

  /** Decline a post-purchase offer. */
  async declinePostPurchaseOffer(opts: {
    orderId: string;
    offerId: string;
  }): Promise<{ success: boolean; error?: string }> {
    return this.http.post(
      `/api/v1/orders/${opts.orderId}/post-purchase-offers/${opts.offerId}/decline`,
    );
  }
}
